import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Download, ChevronsUpDown } from "lucide-react";

// Sample billing plan data - replace with actual data from your backend
const billingPlans = [
	{
		id: "starter",
		name: "Starter",
		price: 49,
		users: 5,
		description: "For small teams getting started",
	},
	{
		id: "professional",
		name: "Professional",
		price: 99,
		users: 10,
		description: "For growing companies",
	},
	{
		id: "enterprise",
		name: "Enterprise",
		price: 199,
		users: 25,
		description: "For large organizations with complex needs",
	},
	{
		id: "custom",
		name: "Custom",
		price: null,
		users: null,
		description: "Custom plan tailored to your needs",
	},
];

// Sample invoice data - replace with actual data from your backend
const invoices = [
	{
		id: "INV-001",
		date: "2025-04-01",
		amount: 99,
		status: "paid",
	},
	{
		id: "INV-002",
		date: "2025-03-01",
		amount: 99,
		status: "paid",
	},
	{
		id: "INV-003",
		date: "2025-02-01",
		amount: 49,
		status: "paid",
	},
];

export const BillingTable = () => {
	const { t } = useTranslation();
	const [selectedPlan, setSelectedPlan] = useState("professional");
	const [cardNumber, setCardNumber] = useState("•••• •••• •••• 4242");
	const [expiryDate, setExpiryDate] = useState("04/26");

	// Get the current plan details based on selectedPlan
	const currentPlan = billingPlans.find((plan) => plan.id === selectedPlan) || billingPlans[0];

	const handlePlanChange = (planId: string) => {
		setSelectedPlan(planId);
	};

	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return new Intl.DateTimeFormat("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		}).format(date);
	};

	return (
		<div className="space-y-8">
			{/* Current Plan Information */}
			<Card>
				<CardHeader>
					<CardTitle>{t("admin.billing.currentPlan")}</CardTitle>
					<CardDescription>
						{t("admin.billing.managePlan")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-6">
						<div className="flex flex-col gap-2">
							<div className="text-lg font-semibold">
								{currentPlan.name} {t("admin.billing.plan")}
							</div>
							<div className="text-sm text-muted-foreground">
								{currentPlan.description}
							</div>
							<div className="mt-2 flex items-center text-lg font-semibold">
								€{currentPlan.price || "Custom"}{" "}
								<span className="text-sm font-normal text-muted-foreground">
									/ {t("admin.billing.month")}
								</span>
							</div>
							<div className="text-sm text-muted-foreground">
								{currentPlan.users
									? t("admin.billing.includesUsers", { count: currentPlan.users })
									: t("admin.billing.customUsers")}
							</div>
						</div>

						<div className="grid gap-4">
							<div className="grid grid-cols-2 gap-4">
								<div className="grid gap-2">
									<Label htmlFor="plan">{t("admin.billing.selectPlan")}</Label>
									<Select value={selectedPlan} onValueChange={handlePlanChange}>
										<SelectTrigger id="plan">
											<SelectValue placeholder={t("admin.billing.selectPlan")} />
										</SelectTrigger>
										<SelectContent>
											{billingPlans.map((plan) => (
												<SelectItem key={plan.id} value={plan.id}>
													{plan.name} {plan.price ? `(€${plan.price}/mo)` : ""}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								<div className="grid gap-2">
									<Label htmlFor="additionalUsers">
										{t("admin.billing.additionalUsers")}
									</Label>
									<div className="flex items-center gap-2">
										<Input
											id="additionalUsers"
											type="number"
											placeholder="0"
											min="0"
											className="w-20"
										/>
										<div className="text-sm text-muted-foreground">
											@ €10 / {t("admin.billing.userPerMonth")}
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</CardContent>
				<CardFooter className="flex justify-between">
					<Button variant="outline">{t("admin.billing.cancelPlan")}</Button>
					<Button className="bg-digger-blue hover:bg-digger-blue-light">
						{t("admin.billing.updatePlan")}
					</Button>
				</CardFooter>
			</Card>

			{/* Payment Method */}
			<Card>
				<CardHeader>
					<CardTitle>{t("admin.billing.paymentMethod")}</CardTitle>
					<CardDescription>
						{t("admin.billing.paymentMethodDescription")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="grid gap-6">
						<div className="flex items-center gap-4">
							<CreditCard className="h-6 w-6" />
							<div>
								<div className="font-medium">{t("admin.billing.creditCard")}</div>
								<div className="text-sm text-muted-foreground">
									{cardNumber} • {t("admin.billing.expires")} {expiryDate}
								</div>
							</div>
						</div>

						<div className="grid grid-cols-2 gap-4">
							<div className="grid gap-2">
								<Label htmlFor="cardName">{t("admin.billing.nameOnCard")}</Label>
								<Input
									id="cardName"
									placeholder="John Doe"
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="cardNumber">{t("admin.billing.cardNumber")}</Label>
								<Input
									id="cardNumber"
									placeholder="4242 4242 4242 4242"
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="expiryDate">{t("admin.billing.expiryDate")}</Label>
								<Input
									id="expiryDate"
									placeholder="MM/YY"
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="cvc">CVC</Label>
								<Input
									id="cvc"
									placeholder="123"
								/>
							</div>
						</div>
					</div>
				</CardContent>
				<CardFooter>
					<Button className="ml-auto bg-digger-blue hover:bg-digger-blue-light">
						{t("admin.billing.updatePaymentMethod")}
					</Button>
				</CardFooter>
			</Card>

			{/* Billing History */}
			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<div>
						<CardTitle>{t("admin.billing.billingHistory")}</CardTitle>
						<CardDescription>
							{t("admin.billing.billingHistoryDescription")}
						</CardDescription>
					</div>
					<div className="flex items-center gap-2">
						<Button variant="outline" size="sm">
							<Download className="h-4 w-4 mr-2" />
							{t("admin.billing.exportAll")}
						</Button>
					</div>
				</CardHeader>
				<CardContent>
					{invoices.length > 0 ? (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("admin.billing.invoice")}</TableHead>
									<TableHead>{t("admin.billing.date")}</TableHead>
									<TableHead>{t("admin.billing.amount")}</TableHead>
									<TableHead>{t("admin.billing.status")}</TableHead>
									<TableHead className="text-right">{t("admin.billing.actions")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{invoices.map((invoice) => (
									<TableRow key={invoice.id}>
										<TableCell className="font-medium">{invoice.id}</TableCell>
										<TableCell>{formatDate(invoice.date)}</TableCell>
										<TableCell>€{invoice.amount.toFixed(2)}</TableCell>
										<TableCell>
											<span
												className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
													invoice.status === "paid"
														? "bg-green-100 text-green-800"
														: "bg-yellow-100 text-yellow-800"
												}`}
											>
												{t(`admin.billing.status.${invoice.status}`)}
											</span>
										</TableCell>
										<TableCell className="text-right">
											<Button variant="ghost" size="sm">
												<Download className="h-4 w-4" />
												<span className="sr-only">{t("admin.billing.download")}</span>
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					) : (
						<div className="flex h-24 items-center justify-center text-muted-foreground">
							{t("admin.billing.noInvoices")}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
};